import { useQuery } from '@tanstack/react-query';
import { ImpersonationBroker } from '../../../extViewmodels';
import { IImageTemplateBrandPreview } from '../../../models/ImageTemplate';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'admin/imageTemplates/<templateId>/brandPreview';

interface IBrandPreviewKey {
	accountId?: string;
	lastModifiedDate?: string;
	templateId: string;
	version: number;
}

const brandPreviewQueryKey = ({ accountId, lastModifiedDate, templateId, version }: IBrandPreviewKey) => [
	BASE_QUERY_KEY,
	templateId,
	version,
	lastModifiedDate,
	accountId,
];

export function useAdminImageTemplateBrandPreviewQuery({
	enabled = true,
	...key
}: IBrandPreviewKey & {
	enabled?: boolean;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!key.templateId && !!key.accountId,
		queryFn: ({ signal }) =>
			userSession.webServiceHelper.callAsync<IImageTemplateBrandPreview>({
				path: ImpersonationBroker.composeApiUrl({
					urlPath: `admin/imageTemplates/${encodeURIComponent(key.templateId)}/brandPreview`,
					queryParams: { accountId: key.accountId },
				}),
				method: 'GET',
				signal,
			}),
		queryKey: brandPreviewQueryKey(key),
		// Each render costs 15-30s of server time, so only an explicit action may start one.
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		retry: false,
		// Switching back to a company within a few minutes is instant; after that its live brand settings may have changed.
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
		meta: {
			errorLogging: { fn: 'useAdminImageTemplateBrandPreviewQuery' },
		},
	});
}

export const cancelAdminImageTemplateBrandPreviewQuery = (key: IBrandPreviewKey) =>
	reactQueryClient.cancelQueries({ queryKey: brandPreviewQueryKey(key) });
